import React, { useEffect, useState, useRef } from 'react';
import SingleSelectCheckboxGroup from '../shared/SingleSelectCheckboxGroup';
import SingleYNCheckbox from '../shared/SingleYNCheckbox';
import { handleZipBlur } from '../../utils/zipAutoPopulate';
import { phoneOnInput, phoneOnBlur, ssnOnInput, ssnOnBlur } from '../../utils/format';
import ZipCityPopup from '../shared/ZipCityPopup';
import { getAccessToken } from '../../services/authApi';
import US_STATES from '../../data/usStates';

const MARITAL_STATUS_MAP = {
    "Married (include registered domestic partners)": "Married",
    "Unmarried (include single, divorced, widowed)": "Unmarried",
    "Separated": "Separated"
};

const RESIDENCY_STATUS_MAP = {
    "US Citizen": "US Citizen",
    "Permanent Resident Alien": "Permanent Resident Alien",
    "Non-Permanent Resident Alien": "Non-Permanent Resident Alien",
    "Foreign National": "Foreign National",
    "ITIN": "ITIN"
};

const ADDRESS_TYPE_MAP = {
    "Own": "Own",
    "Rent": "Rent",
    "Living Rent Free": "NoPrimaryHousingExpense"
};

const ApplicantInformation = ({ fieldValues, showEmployment, borrowerIndex, showWarningToast, setWarningMessage }) => {
    const [applicantMarital, setApplicantMarital] = useState("");
    const [coApplicantMarital, setCoApplicantMarital] = useState("");
    const [applicantResidency, setApplicantResidency] = useState("");
    const [coApplicantResidency, setCoApplicantResidency] = useState("");

    const [applicantAddressType, setApplicantAddressType] = useState("");
    const [coApplicantAddressType, setCoApplicantAddressType] = useState("");
    const [applicantFormerAddressType, setApplicantFormerAddressType] = useState("");
    const [coApplicantFormerAddressType, setCoApplicantFormerAddressType] = useState("");

    const [localFieldValues, setLocalFieldValues] = useState({});

    const [token, setToken] = useState(null);
    const [zipPopup, setZipPopup] = useState({ results: [], cityFieldId: '', stateFieldId: '' });

    const [borrowerUsCredit, setBorrowerUsCredit] = useState("");
    const [coBorrowerUsCredit, setCoBorrowerUsCredit] = useState("");

    const [applicantPresentForeign, setApplicantPresentForeign] = useState("");
    const [coApplicantPresentForeign, setCoApplicantPresentForeign] = useState("");
    const [applicantFormerForeign, setApplicantFormerForeign] = useState("");
    const [coApplicantFormerForeign, setCoApplicantFormerForeign] = useState("");
    const [applicantMailingForeign, setApplicantMailingForeign] = useState("");
    const [coApplicantMailingForeign, setCoApplicantMailingForeign] = useState("");

    const isBorrowerSwitchRef = useRef(false);
    const prevBorrowerIndexRef = useRef(borrowerIndex);

    const isYes = (val) => (val || "").toString().trim().toUpperCase() === "Y";

    useEffect(() => {
        if (prevBorrowerIndexRef.current !== borrowerIndex) {
            isBorrowerSwitchRef.current = true;
            prevBorrowerIndexRef.current = borrowerIndex;
            setLocalFieldValues({});
        }
    }, [borrowerIndex]);

    useEffect(() => {
        setApplicantAddressType(fieldValues["FR0115"] || "");
        setCoApplicantAddressType(fieldValues["FR0215"] || "");
        setApplicantFormerAddressType(fieldValues["FR0315"] || "");
        setCoApplicantFormerAddressType(fieldValues["FR0415"] || "");

        setApplicantMarital(fieldValues["52"] || "");
        setCoApplicantMarital(fieldValues["84"] || "");
        setApplicantResidency(fieldValues["CX.CITIZENSHIP.TYPE"] || "");
        setCoApplicantResidency(fieldValues["CX.CITIZENSHIP.TYPE.COB"] || "");

        setBorrowerUsCredit(fieldValues["CX.US.CREDIT.HIST.BOR.P1"] || "");
        setCoBorrowerUsCredit(fieldValues["CX.US.CREDIT.HIST.COB.P1"] || "");

        setApplicantPresentForeign(fieldValues["FR0129"] || "");
        setCoApplicantPresentForeign(fieldValues["FR0229"] || "");
        setApplicantFormerForeign(fieldValues["FR0329"] || "");
        setCoApplicantFormerForeign(fieldValues["FR0429"] || "");
        setApplicantMailingForeign(fieldValues["URLA.X267"] || "");
        setCoApplicantMailingForeign(fieldValues["URLA.X268"] || "");

        const streetIds = [
            "FR0126", "FR0226", "FR0326", "FR0426",
            "URLA.X197", "URLA.X198",
            "FE0160", "FE0260"
        ];

        setLocalFieldValues(prev => {
            // borrower pair switched → take fresh values exactly as-is
            if (isBorrowerSwitchRef.current) {
                isBorrowerSwitchRef.current = false;
                return fieldValues || {};
            }

            // normal refresh after save → preserve street if backend temporarily sends blank
            const next = { ...(fieldValues || {}) };

            streetIds.forEach(id => {
                const incoming = next[id];
                const prevVal = prev?.[id];

                if ((incoming === "" || incoming === "//" || incoming == null) && prevVal) {
                    next[id] = prevVal;
                }
            });

            return next;
        });
    }, [fieldValues]);

    // Re-apply state dropdown values after fieldValues update (especially after Save)
    useEffect(() => {
        const dropdownIds = ['FR0107', 'FR0207', '1418', '1521', 'FR0307', 'FR0407', 'FE0106', 'FE0206'];

        dropdownIds.forEach(id => {
            const el = document.getElementById(`field-${id}`);
            if (el && fieldValues[id] !== undefined) {
                el.value = fieldValues[id];
            }
        });
    }, [fieldValues]);

    const parseMoney = (v) => {
        const s = (v ?? "").toString().replace(/,/g, "").trim();
        if (!s) return 0;
        const n = parseFloat(s);
        return isNaN(n) ? 0 : n;
    };

    const formatMoney2 = (n) => {
        if (n === null || n === undefined) return "";
        const num = Number(n);
        if (isNaN(num)) return "";
        return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };
    const calcIncomeTotal = (ids) => {
        const total =
            parseMoney(localFieldValues[ids.base]) +
            parseMoney(localFieldValues[ids.commission]) +
            parseMoney(localFieldValues[ids.overtime]) +
            parseMoney(localFieldValues[ids.bonus]) +
            parseMoney(localFieldValues[ids.other]);

        return formatMoney2(total);
    };

    const renderCheckboxGroup = (labelText, fieldId, optionsMap, selectedValue, setSelectedValue, nameAttr) => (
        <div className="mt-3">
            <label className="form-label">{labelText}</label>
            <SingleSelectCheckboxGroup
                fieldId={fieldId}
                name={nameAttr}
                optionsMap={optionsMap}
                selectedValue={selectedValue}
                onSelect={setSelectedValue}
            />
        </div>
    );

    const streetField = (label, id) => {
        const value =
            localFieldValues[id] === '//'
                ? ''
                : (localFieldValues[id] ?? fieldValues[id] ?? '');

        return (
            <div className='mt-2'>
                <label className="form-label">{label}</label>
                <input
                    type="text"
                    className="form-input"
                    id={`field-${id}`}
                    value={value}
                    onChange={(e) => setFieldValue(id, e.target.value)}
                />
            </div>
        );
    };

    const textField = (label, id, cityId = null, stateId = null, skipZipLookup = false) => {
        const value = localFieldValues[id] === '//' ? '' : (localFieldValues[id] ?? fieldValues[id] ?? '');

        return (
            <div className='mt-2'>
                <label className="form-label">{label}</label>
                <input
                    type="text"
                    className="form-input"
                    id={`field-${id}`}
                    value={value}
                    onChange={(e) => setFieldValue(id, e.target.value)}
                    onBlur={
                        cityId && stateId && !skipZipLookup
                            ? async () => {
                                await handleZipCodeBlur(id, cityId, stateId);

                                const zipVal = document.getElementById(`field-${id}`)?.value || "";
                                const cityVal = document.getElementById(`field-${cityId}`)?.value || "";
                                const stateVal = document.getElementById(`field-${stateId}`)?.value || "";

                                setLocalFieldValues(prev => ({
                                    ...prev,
                                    [id]: zipVal,
                                    [cityId]: cityVal,
                                    [stateId]: stateVal
                                }));
                            }
                            : undefined
                    }
                />
            </div>
        );
    };

    const emailField = (label, id) => (
        <div className='mt-2'>
            <label className="form-label">{label}</label>
            <input
                type="text"
                className="form-input"
                id={`field-${id}`}
                value={localFieldValues[id] ?? fieldValues[id] ?? ''}
                onChange={(e) => setFieldValue(id, e.target.value)}
                onBlur={(e) => emailOnBlur(e, 'Applicant Email')}
            />
        </div>
    );

    const phoneField = (label, id) => (
        <div className='mt-2'>
            <label className="form-label">{label}</label>
            <input
                type="text"
                className="form-input"
                id={`field-${id}`}
                value={localFieldValues[id] ?? fieldValues[id] ?? ''}
                onChange={(e) => {
                    phoneOnInput(e);
                    setFieldValue(id, e.target.value);
                }}
                onBlur={(e) => {
                    phoneOnBlur(e);
                    setFieldValue(id, e.target.value);
                }}
            />
        </div>
    );

    const ssnField = (label, id) => (
        <div className='mt-2'>
            <label className="form-label">{label}</label>
            <input
                type="text"
                className="form-input"
                id={`field-${id}`}
                value={localFieldValues[id] ?? fieldValues[id] ?? ''}
                onChange={(e) => {
                    ssnOnInput(e);
                    setFieldValue(id, e.target.value);
                }}
                onBlur={(e) => {
                    ssnOnBlur(e);
                    setFieldValue(id, e.target.value);
                }}
            />
        </div>
    );

    const lable = (label) => (
        <div className='mt-3'>
            <label className="form-label-semibold">{label}</label>
        </div>
    );

    const moneyField = (label, id, readOnly = false) => {
        const value = localFieldValues[id] === '//' ? '' : (localFieldValues[id] || '');
        return (
            <div className='mt-2'>
                <label className="form-label">{label}</label>
                <input
                    type="text"
                    className="form-input"
                    id={`field-${id}`}
                    value={value}
                    readOnly={readOnly}
                    onChange={(e) => {
                        if (readOnly) return;
                        const raw = e.target.value || "";
                        // allow typing commas/dot/digits; don’t hard-format while typing
                        setLocalFieldValues(prev => ({ ...prev, [id]: raw }));
                    }}
                />
            </div>
        );
    };


    const formatDateInput = (value) => {
        // Remove non-digits
        const cleaned = value.replace(/\D/g, '');

        // Format MM/DD/YYYY
        let formatted = '';
        if (cleaned.length <= 2) {
            formatted = cleaned;
        } else if (cleaned.length <= 4) {
            formatted = `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
        } else {
            formatted = `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
        }

        return formatted;
    };

    const validateDate = (dateStr) => {
        // Check format first
        if (!/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return false;

        const [month, day, year] = dateStr.split('/').map(Number);

        // Check valid month
        if (month < 1 || month > 12) return false;

        // Check valid day range for given month/year
        const testDate = new Date(year, month - 1, day);

        return (
            testDate.getFullYear() === year &&
            testDate.getMonth() === month - 1 &&
            testDate.getDate() === day
        );
    };

    const fetchToken = async () => {
        const authObject = await window.elli.script.getObject("auth");
        const authCode = await authObject.createAuthCode();
        const token = await getAccessToken(authCode);
        setToken(token);
        return token;
    };

    const handleZipCodeBlur = async (zipId, cityId, stateId) => {
        const tokenToUse = token || await fetchToken();

        await handleZipBlur({
            zipFieldId: zipId.startsWith("field-") ? zipId : `field-${zipId}`,
            cityFieldId: cityId.startsWith("field-") ? cityId : `field-${cityId}`,
            stateFieldId: stateId.startsWith("field-") ? stateId : `field-${stateId}`,
            token: tokenToUse,
            setPopupOptions: ({ results, cityFieldId, stateFieldId }) =>
                setZipPopup({ results, cityFieldId, stateFieldId })
        });

        const zipKey = zipId.replace("field-", "");
        const cityKey = cityId.replace("field-", "");
        const stateKey = stateId.replace("field-", "");

        const zipVal = document.getElementById(`field-${zipKey}`)?.value || "";
        const cityVal = document.getElementById(`field-${cityKey}`)?.value || "";
        const stateVal = document.getElementById(`field-${stateKey}`)?.value || "";

        setLocalFieldValues(prev => ({
            ...prev,
            [zipKey]: zipVal,
            [cityKey]: cityVal,
            [stateKey]: stateVal
        }));
    };

    const handleChange = (e, id) => {
        const input = e.target.value;
        const formattedInput = formatDateInput(input);
        setLocalFieldValues(prev => ({ ...prev, [id]: formattedInput }));
    };

    const handleBlur = (e, id) => {
        const value = e.target.value;
        if (value && !validateDate(value)) {
            alert('Invalid date format. Please enter as mm/dd/yyyy');
            setLocalFieldValues(prev => ({ ...prev, [id]: '' }));
            e.target.focus();
        }
    };

    const textDateField = (label, id) => {
        const value =
            localFieldValues[id] === '//'
                ? ''
                : (localFieldValues[id] ?? fieldValues[id] ?? '');

        return (
            <div className='mt-2'>
                <label className="form-label">{label}</label>
                <input
                    type="text"
                    className="form-input"
                    placeholder="mm/dd/yyyy"
                    id={`field-${id}`}
                    value={value}
                    maxLength={10}
                    onChange={(e) => handleChange(e, id)}
                    onBlur={(e) => handleBlur(e, id)}
                />
            </div>
        );
    };

    const labelField = (label) => (
        <div>
            <label className="form-label">{label}</label>
        </div>
    );


    const setFieldValue = (id, value) => {
        setLocalFieldValues(prev => ({ ...prev, [id]: value }));
    };

    const renderStateField = (label, id, isForeign) => {
        const value = localFieldValues[id] ?? fieldValues[id] ?? '';

        return (
            <div className="mt-2">
                <label className="form-label">{label}</label>
                {isForeign ? (
                    <input
                        type="text"
                        id={`field-${id}`}
                        className="form-input"
                        value={value}
                        onChange={(e) => setFieldValue(id, e.target.value)}
                    />
                ) : (
                    <select
                        id={`field-${id}`}
                        className="form-input w-full text-sm border rounded p-1"
                        value={value}
                        onChange={(e) => setFieldValue(id, e.target.value)}
                    >
                        <option value="">Select State</option>
                        {US_STATES.map((st) => (
                            <option key={st.abbrev} value={st.abbrev}>
                                {st.name}
                            </option>
                        ))}
                    </select>
                )}
            </div>
        );
    };

    const renderCountryField = (label, id, isForeign, zipId) => {
        const zip = (localFieldValues[zipId] ?? fieldValues[zipId] ?? '').toString().trim();
        const rawValue = localFieldValues[id] ?? fieldValues[id] ?? '';

        const value = !zip
            ? ''
            : (isForeign ? rawValue : 'US');

        return (
            <div className='mt-2'>
                <label className="form-label">{label}</label>
                <input
                    type="text"
                    className="form-input"
                    id={`field-${id}`}
                    value={value}
                    readOnly={!isForeign || !zip}
                    onChange={(e) => {
                        if (isForeign && zip) {
                            setFieldValue(id, e.target.value);
                        }
                    }}
                />
            </div>
        );
    };

    const applyForeignRules = (isForeign, countryId) => {
        setLocalFieldValues(prev => {
            const next = { ...prev };
            const currentCountry = (next[countryId] ?? fieldValues[countryId] ?? "").toString().trim().toUpperCase();

            if (isForeign) {
                if (currentCountry === "US") {
                    next[countryId] = "";
                }
            } else {
                next[countryId] = "US";
            }

            return next;
        });
    };

    useEffect(() => {
        applyForeignRules(isYes(applicantPresentForeign), "FR0130");
    }, [applicantPresentForeign]);

    useEffect(() => {
        applyForeignRules(isYes(applicantMailingForeign), "URLA.X269");
    }, [applicantMailingForeign]);

    useEffect(() => {
        applyForeignRules(isYes(applicantFormerForeign), "FR0330");
    }, [applicantFormerForeign]);

    useEffect(() => {
        applyForeignRules(isYes(coApplicantPresentForeign), "FR0230");
    }, [coApplicantPresentForeign]);

    useEffect(() => {
        applyForeignRules(isYes(coApplicantMailingForeign), "URLA.X270");
    }, [coApplicantMailingForeign]);

    useEffect(() => {
        applyForeignRules(isYes(coApplicantFormerForeign), "FR0430");
    }, [coApplicantFormerForeign]);

    const addressFields = (
        prefix = '',
        ids = {},
        selectedValue,
        setSelectedValue,
        presentForeignValue,
        setPresentForeignValue,
        mailingForeignValue,
        setMailingForeignValue
    ) => {
        const isPresentForeign = isYes(presentForeignValue);
        const isMailingForeign = isYes(mailingForeignValue);

        return (
            <>
                {lable('Present Address (street, city, state, ZIP)')}
                <SingleYNCheckbox
                    fieldId={ids.presentForeign}
                    label="Foreign Address"
                    checkedValue={presentForeignValue}
                    setCheckedValue={setPresentForeignValue}
                />

                {streetField(`${prefix}Street`, ids.address)}
                {textField(`City`, ids.city)}
                {renderStateField("State", ids.state, isPresentForeign)}
                {textField(`Zip`, ids.zip, ids.city, ids.state, isPresentForeign)}
                {renderCountryField("Country", ids.country, isPresentForeign, ids.zip)}

                <div className='mt-2'>
                    <label className="form-label">Housing</label>
                </div>

                <div className="flex items-center gap-4 mt-2 flex-wrap">
                    {["Own", "Rent"].map((label) => {
                        const value = ADDRESS_TYPE_MAP[label];
                        return (
                            <label key={value} className="checkbox-label flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    className="checkbox-accent"
                                    id={`field-${ids.own}`}
                                    name={`field-${ids.own}`}
                                    value={value}
                                    checked={selectedValue === value}
                                    onChange={() => {
                                        const newValue = selectedValue === value ? "" : value;
                                        setSelectedValue(newValue);
                                        const group = document.querySelectorAll(`input[name="field-${ids.own}"]`);
                                        group.forEach(cb => cb.checked = (cb.value === newValue));
                                    }}
                                />
                                {label}
                                {label === "Rent" && (
                                    <div className="flex items-center gap-2 ml-4">
                                        <label className="form-label w-[60px]">No. Yrs.</label>
                                        <input
                                            type="text"
                                            className="form-input-small"
                                            id={`field-${ids.years}`}
                                            value={localFieldValues[ids.years] ?? fieldValues[ids.years] ?? ""}
                                            onChange={(e) => setFieldValue(ids.years, e.target.value)}
                                        />
                                    </div>
                                )}
                            </label>
                        );
                    })}
                </div>

                <div className="flex items-center gap-4 mt-2">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            className="checkbox-accent"
                            id={`field-${ids.own}`}
                            name={`field-${ids.own}`}
                            value="NoPrimaryHousingExpense"
                            checked={selectedValue === "NoPrimaryHousingExpense"}
                            onChange={() => {
                                setSelectedValue("NoPrimaryHousingExpense");
                                const group = document.querySelectorAll(`input[name="field-${ids.own}"]`);
                                group.forEach(cb => cb.checked = (cb.value === "NoPrimaryHousingExpense"));
                            }}
                        />
                        Living Rent Free
                    </label>
                </div>

                {lable('Mailing Address, if different from Present Address')}

                <SingleYNCheckbox
                    fieldId={ids.mailingForeign}
                    label="Mailing Foreign Address"
                    checkedValue={mailingForeignValue}
                    setCheckedValue={setMailingForeignValue}
                />

                {streetField(`${prefix}Street`, ids.mailing)}
                {textField(`City`, ids.mailingcity)}
                {renderStateField("State", ids.mailingstate, isMailingForeign)}
                {textField(`Zip`, ids.mailingzip, ids.mailingcity, ids.mailingstate, isMailingForeign)}
                {renderCountryField("Country", ids.mailingCountry, isMailingForeign, ids.mailingzip)}
            </>
        );
    };

    const formerAddressSection = (
        labelPrefix,
        fieldPrefix,
        fieldValues,
        selectedValue,
        setSelectedValue,
        foreignValue,
        setForeignValue
    ) => {
        const addressField = `${fieldPrefix}26`;
        const cityField = `${fieldPrefix}06`;
        const stateField = `${fieldPrefix}07`;
        const zipField = `${fieldPrefix}08`;
        const typeField = `${fieldPrefix}15`;
        const yearsField = `${fieldPrefix}12`;
        const foreignField = `${fieldPrefix}29`;
        const countryField = `${fieldPrefix}30`;

        const isForeign = isYes(foreignValue);

        return (
            <div>
                {lable(`${labelPrefix} (street, city, state, ZIP)`)}
                <SingleYNCheckbox
                    fieldId={foreignField}
                    label="Foreign Address"
                    checkedValue={foreignValue}
                    setCheckedValue={setForeignValue}
                />

                {streetField(`Street`, addressField)}
                {textField('City', cityField)}
                {renderStateField("State", stateField, isForeign)}
                {textField(`Zip`, zipField, cityField, stateField, isForeign)}
                {renderCountryField("Country", countryField, isForeign, zipField)}

                <div className='mt-2'>
                    <label className="form-label">Housing</label>
                </div>

                <div className="flex items-center gap-4 mt-2">
                    {["Own", "Rent"].map((option) => (
                        <React.Fragment key={option}>
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    className="checkbox-accent"
                                    id={`field-${typeField}`}
                                    name={`field-${typeField}`}
                                    value={option}
                                    checked={selectedValue === option}
                                    onChange={() => {
                                        const newValue = selectedValue === option ? "" : option;
                                        setSelectedValue(newValue);
                                        const group = document.querySelectorAll(`input[name="${typeField}"]`);
                                        group.forEach(cb => cb.checked = (cb.value === newValue));
                                    }}
                                />
                                {option}
                            </label>

                            {option === "Rent" && (
                                <div className="flex items-center gap-2 ml-4">
                                    <label className="form-label w-[60px]">No. Yrs.</label>
                                    <input
                                        type="text"
                                        className="form-input-small"
                                        id={`field-${yearsField}`}
                                        value={localFieldValues[yearsField] ?? fieldValues[yearsField] ?? ""}
                                        onChange={(e) => setFieldValue(yearsField, e.target.value)}
                                    />
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        );
    };

    const employmentSection = (prefix = '', ids = {}) => (
        <>
            {textField(`${prefix}Employer Name`, ids.name)}
            {streetField(`${prefix}Employer Address`, ids.address)}
            {textField(`City`, ids.city)}
            {/* <StateDropdown label="State" id={ids.state} /> */}
            <div className="mt-2">
                <label className="form-label">State</label>
                <select
                    id={`field-${ids.state}`}
                    className="form-input w-full text-sm border rounded p-1"
                    value={localFieldValues[ids.state] ?? fieldValues[ids.state] ?? ""}
                    onChange={(e) => setFieldValue(ids.state, e.target.value)}
                >
                    <option value="">Select State</option>
                    {US_STATES.map((st) => (
                        <option key={st.abbrev} value={st.abbrev}>
                            {st.name}
                        </option>
                    ))}
                </select>
            </div>
            {textField(`Zip`, ids.zip, ids.city, ids.state)}
            {phoneField(`${prefix}Phone Number`, ids.phone)}
            {textField(`${prefix}Job Title`, ids.title)}
            {textField(`${prefix}No. Yrs.`, ids.years)}
            {textField(`${prefix}No. Months.`, ids.months)}
        </>
    );

    const incomeSection = (prefix = '', ids = {}) => {
        const total = calcIncomeTotal(ids);

        return (
            <>
                {moneyField(`${prefix}Base Income`, ids.base)}
                {moneyField(`${prefix}Commission`, ids.commission)}
                {moneyField(`${prefix}Overtime`, ids.overtime)}
                {moneyField(`${prefix}Bonus`, ids.bonus)}
                {moneyField(`${prefix}Other Income`, ids.other)}

                <div className="mt-2">
                    <label className="form-label">{`${prefix}Total`}</label>
                    <input
                        type="text"
                        className="form-input"
                        value={total}
                        readOnly
                        tabIndex={-1}
                    />
                </div>
            </>
        );
    };


    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const emailOnBlur = (e, fieldLabel = 'Email') => {
        const val = (e.target.value || "").trim();
        if (!val) return; // allow blank unless you want required
        if (!emailRegex.test(val)) {
            const msg = `${fieldLabel} is invalid. Please enter a valid email address.`;
            // use parent's toast
            if (typeof setWarningMessage === 'function') setWarningMessage(msg);
            if (typeof showWarningToast === 'function') {
                showWarningToast(true);
                setTimeout(() => showWarningToast(false), 4500); // match Index timing
            }
            e.target.focus();
        }
    };

    useEffect(() => {
        const isFN = ((applicantResidency || "").toUpperCase() === "FOREIGN NATIONAL");
        if (!isFN) {
            setBorrowerUsCredit("");
            // uncheck UI checkboxes (SingleSelectCheckboxGroup uses same id for the group)
            const escaped = "CX.US.CREDIT.HIST.BOR.P1".replace(/\./g, "\\.");
            document.querySelectorAll(`input[type="checkbox"]#field-${escaped}`).forEach(cb => cb.checked = false);
        }
    }, [applicantResidency]);

    useEffect(() => {
        const isFN = ((coApplicantResidency || "").toUpperCase() === "FOREIGN NATIONAL");
        if (!isFN) {
            setCoBorrowerUsCredit("");
            const escaped = "CX.US.CREDIT.HIST.COB.P1".replace(/\./g, "\\.");
            document.querySelectorAll(`input[type="checkbox"]#field-${escaped}`).forEach(cb => cb.checked = false);
        }
    }, [coApplicantResidency]);


    return (
        <>
            <ZipCityPopup
                visible={zipPopup.results.length > 1}
                results={zipPopup.results}
                onSelect={(opt) => {
                    const cityInput = document.getElementById(zipPopup.cityFieldId);
                    const stateInput = document.getElementById(zipPopup.stateFieldId);

                    if (cityInput) cityInput.value = opt.city;
                    if (stateInput) stateInput.value = opt.abbrev;

                    if (zipPopup.cityFieldId?.startsWith("field-")) {
                        const cityId = zipPopup.cityFieldId.replace("field-", "");
                        setFieldValue(cityId, opt.city);
                    }
                    if (zipPopup.stateFieldId?.startsWith("field-")) {
                        const stateId = zipPopup.stateFieldId.replace("field-", "");
                        setFieldValue(stateId, opt.abbrev);
                    }

                    setZipPopup({ results: [], cityFieldId: '', stateFieldId: '' });
                }}
                onClose={() => setZipPopup({ results: [], cityFieldId: '', stateFieldId: '' })}
            />


            <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9] mb-3">
                <div className="section-header">3. Applicant Information (Borrower and/or Guarantor)</div>

                <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr] p-4 border-b border-[var(--blue-2)] bg-[var(--white)]">
                    <div className="font-semibold text-[var(--slate-grey-2)] text-sm">Applicant</div>
                    <div className="font-semibold text-[var(--slate-grey-2)] text-sm">Co-Applicant</div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 p-4">
                    {/* Applicant */}
                    <div>
                        {labelField("Applicant’s Name (include Jr. or Sr. if applicable)")}
                        {textField("First Name", "4000")}
                        {textField("Middle", "4001")}
                        {textField("Last Name", "4002")}
                        {textField("Suffix", "4003")}
                        {ssnField('Social Security Number', "65")}
                        {phoneField('Home Phone (Incl. area code)', "66")}
                        {phoneField('Cell Phone', "1490")}
                        {emailField('Email', "1240")}
                        {textDateField('DOB (mm/dd/yyyy)', "1402")}
                        {renderCheckboxGroup("Marital Status", "52", MARITAL_STATUS_MAP, applicantMarital, setApplicantMarital, "applicant-marital")}
                        {renderCheckboxGroup("Residency Status", "CX.CITIZENSHIP.TYPE", RESIDENCY_STATUS_MAP, applicantResidency, setApplicantResidency, "applicant-residency")}
                        {/* Borrower US Credit History (only if Pair 1 + Foreign National/ITIN) */}
                        {borrowerIndex === 1 &&
                            ["FOREIGN NATIONAL"].includes((applicantResidency || "").toUpperCase()) && (
                                <div className="mt-3">
                                    <label className="form-label">Does Foreign National Borrower have U.S. Credit?</label>
                                    <SingleSelectCheckboxGroup
                                        fieldId="CX.US.CREDIT.HIST.BOR.P1"
                                        name="borrower-us-credit-history"
                                        optionsMap={{ Yes: "Y", No: "N" }}
                                        selectedValue={borrowerUsCredit}
                                        onSelect={(val) => setBorrowerUsCredit(val)}
                                    />
                                </div>
                            )}
                        {addressFields('', {
                            address: 'FR0126',
                            city: 'FR0106',
                            state: 'FR0107',
                            zip: 'FR0108',
                            country: 'FR0130',
                            presentForeign: 'FR0129',
                            own: 'FR0115',
                            years: 'FR0112',
                            mailing: 'URLA.X197',
                            mailingcity: '1417',
                            mailingstate: '1418',
                            mailingzip: '1419',
                            mailingCountry: 'URLA.X269',
                            mailingForeign: 'URLA.X267'
                        },
                            applicantAddressType,
                            setApplicantAddressType,
                            applicantPresentForeign,
                            setApplicantPresentForeign,
                            applicantMailingForeign,
                            setApplicantMailingForeign)}

                    </div>

                    {/* Co-Applicant */}
                    <div>
                        {labelField("Co-Applicant’s Name (include Jr. or Sr. if applicable)")}
                        {textField("First Name", "4004")}
                        {textField("Middle", "4005")}
                        {textField("Last Name", "4006")}
                        {textField("Suffix", "4007")}
                        {ssnField('Social Security Number', "97")}
                        {phoneField('Home Phone (Incl. area code)', "98")}
                        {phoneField('Cell Phone', "1480")}
                        {emailField('Email', "1268")}
                        {textDateField('DOB (mm/dd/yyyy)', "1403")}
                        {renderCheckboxGroup("Marital Status", "84", MARITAL_STATUS_MAP, coApplicantMarital, setCoApplicantMarital, "coapplicant-marital")}
                        {renderCheckboxGroup("Residency Status", "CX.CITIZENSHIP.TYPE.COB", RESIDENCY_STATUS_MAP, coApplicantResidency, setCoApplicantResidency, "coapplicant-residency")}

                        {/* Co-Borrower US Credit History (only if Pair 1 + Foreign National/ITIN) */}
                        {borrowerIndex === 1 &&
                            ["FOREIGN NATIONAL"].includes((coApplicantResidency || "").toUpperCase()) && (
                                <div className="mt-3">
                                    <label className="form-label">Does Foreign National Co-Borrower have U.S. Credit?</label>
                                    <SingleSelectCheckboxGroup
                                        fieldId="CX.US.CREDIT.HIST.COB.P1"
                                        name="coborrower-us-credit-history"
                                        optionsMap={{ Yes: "Y", No: "N" }}
                                        selectedValue={coBorrowerUsCredit}
                                        onSelect={(val) => setCoBorrowerUsCredit(val)}
                                    />
                                </div>
                            )}
                        {addressFields('', {
                            address: 'FR0226',
                            city: 'FR0206',
                            state: 'FR0207',
                            zip: 'FR0208',
                            country: 'FR0230',
                            presentForeign: 'FR0229',
                            own: 'FR0215',
                            years: 'FR0212',
                            mailing: 'URLA.X198',
                            mailingcity: '1520',
                            mailingstate: '1521',
                            mailingzip: '1522',
                            mailingCountry: 'URLA.X270',
                            mailingForeign: 'URLA.X268'
                        },
                            coApplicantAddressType,
                            setCoApplicantAddressType,
                            coApplicantPresentForeign,
                            setCoApplicantPresentForeign,
                            coApplicantMailingForeign,
                            setCoApplicantMailingForeign)}


                    </div>
                </div>


                <div className="grid md:grid-cols-1 gap-6 pt-4 pl-4 pr-4">
                    <label className="form-label-bold">
                        If residing at present address for less than two years, complete the following:
                    </label>
                </div>


                <div className="grid md:grid-cols-2 gap-6  pb-4 pl-4 pr-4">

                    {/* Applicant */}
                    <div>
                        {formerAddressSection(
                            "Former Address",
                            "FR03",
                            fieldValues,
                            applicantFormerAddressType,
                            setApplicantFormerAddressType,
                            applicantFormerForeign,
                            setApplicantFormerForeign
                        )}
                    </div>

                    {/* Co-Applicant */}
                    <div>
                        {formerAddressSection(
                            "Former Address",
                            "FR04",
                            fieldValues,
                            coApplicantFormerAddressType,
                            setCoApplicantFormerAddressType,
                            coApplicantFormerForeign,
                            setCoApplicantFormerForeign
                        )}
                    </div>


                </div>
                {showEmployment && (
                    <>
                        <div className="grid md:grid-cols-1 gap-6 pt-4 pl-4 pr-4">
                            <label className="form-label-bold">
                                Employment
                            </label>
                        </div>
                        <div className="grid md:grid-cols-2 gap-6  pb-4 pl-4 pr-4">
                            <div>
                                {employmentSection('', {
                                    name: 'FE0102',
                                    address: 'FE0160',
                                    city: 'FE0105',
                                    state: 'FE0106',
                                    zip: 'FE0107',
                                    phone: 'FE0117',
                                    title: 'FE0110',
                                    years: 'FE0116',
                                    months: 'FE0152'
                                })}
                            </div>

                            {/* Co-Applicant */}
                            <div>
                                {employmentSection('', {
                                    name: 'FE0202',
                                    address: 'FE0260',
                                    city: 'FE0205',
                                    state: 'FE0206',
                                    zip: 'FE0207',
                                    phone: 'FE0217',
                                    title: 'FE0210',
                                    years: 'FE0216',
                                    months: 'FE0252'
                                })}
                            </div>
                        </div>

                        <div className="grid md:grid-cols-1 gap-6 pt-4 pl-4 pr-4">
                            <label className="form-label-bold">
                                Income
                            </label>
                        </div>
                        <div className="grid md:grid-cols-2 gap-6  pb-4 pl-4 pr-4">
                            <div>
                                {incomeSection('', {
                                    base: 'FE0119',
                                    overtime: 'FE0120',
                                    bonus: 'FE0121',
                                    commission: 'FE0122',
                                    other: 'FE0123'
                                })}
                            </div>

                            {/* Co-Applicant */}
                            <div>
                                {incomeSection('', {
                                    base: 'FE0219',
                                    overtime: 'FE0220',
                                    bonus: 'FE0221',
                                    commission: 'FE0222',
                                    other: 'FE0223'
                                })}
                            </div>
                        </div>
                    </>
                )}

            </div>
        </>
    );
};

export default ApplicantInformation;
